import { setMusicDucked } from "./music";

type AudioProvider = "minimax" | "legacy_tingting" | "unknown";
type AudioEntry = { file: string; text: string };
type AudioManifest = {
  entries: Record<string, AudioEntry>;
  files?: Record<
    string,
    { provider: AudioProvider; model?: string; voiceId?: string }
  >;
};

export type SpokenClip = { key: string; text: string };

let player: HTMLAudioElement | null = null;
let muted = false;
let generation = 0;
let manifest: Promise<AudioManifest> | null = null;

export function setMuted(value: boolean) {
  muted = value;
  if (value) stopAudio();
}

export function stopAudio() {
  generation++;
  player?.pause();
  player = null;
  setMusicDucked(false);
}

async function getManifest() {
  manifest ??= fetch("/audio/manifest.json", { cache: "no-cache" })
    .then((response) => {
      if (!response.ok) throw new Error("audio unavailable");
      return response.json() as Promise<AudioManifest>;
    })
    .catch((error) => {
      manifest = null;
      throw error;
    });
  return manifest;
}

async function playClip(key: string, current: number) {
  const audioManifest = await getManifest();
  const entry = audioManifest.entries[key];
  if (!entry) throw new Error(`audio key missing: ${key}`);
  const provider = audioManifest.files?.[entry.file]?.provider ?? "unknown";
  if (provider !== "minimax")
    throw new Error(`unverified audio provider: ${provider}`);
  if (current !== generation || muted) return;
  const audio = new Audio(entry.file);
  player = audio;
  await new Promise<void>((resolve, reject) => {
    audio.addEventListener("ended", () => resolve(), { once: true });
    audio.addEventListener(
      "error",
      () => reject(new Error("audio playback failed")),
      { once: true },
    );
    audio.play().catch(reject);
  });
}

export async function speak(text: string, key?: string): Promise<boolean> {
  if (!key) return false;
  return speakSequence([{ key, text }]);
}

export async function speakSequence(clips: SpokenClip[]): Promise<boolean> {
  stopAudio();
  const current = generation;
  if (muted) return true;
  setMusicDucked(true);
  let allClipsAvailable = true;
  try {
    for (const clip of clips) {
      if (current !== generation || muted) return true;
      try {
        await playClip(clip.key, current);
      } catch {
        allClipsAvailable = false;
        if (current !== generation || muted) return true;
      }
    }
    return allClipsAvailable;
  } catch {
    // Never replace a missing MiniMax clip with the browser or operating
    // system voice. Text remains visible and replay remains available.
    return false;
  } finally {
    if (current === generation) {
      player = null;
      setMusicDucked(false);
    }
  }
}
